

















































import Vue from 'vue';
import {
  Component, Inject, Watch, Prop,
} from 'vue-property-decorator';
import { mapState } from 'vuex';
import { DataTableHeader } from 'vuetify';
import { Alert, NoticeClass } from '@kasasa/fbase-components/lib';
import { RewardDetailsResource, RewardDetails } from '@/api/rewarddetails';
import { Cycle } from '@/api/cycles';
import { Account } from '@/api/consumeraccountdetails';
import { ATMSettingsResource, ATMSettings } from '@/api/atm';
import { InterestSettingsResource, InterestSettings } from '@/api/interestsettings';
import TunesRefundsSheet from '@/components/account/TunesRefundsSheet.vue';

interface RewardDetailTableEntry {
  cycleId: number;
  cycleName: string;
  statementEndDate: string;
  rewardDetails: RewardDetails;
  sortDate: number;
  qualStartDate: string;
  qualEndDate: string;
}

@Component({
  computed: mapState(['fiId']),
  components: {
    TunesRefundsSheet,
  },
})
export default class RewardDetailTable extends Vue {
  @Prop() account!: Account;

  @Prop() cycles!: Cycle[];

  @Prop() relationship!: string;

  rewardItems: RewardDetailTableEntry[] = [];

  fiId!: string;

  loading: boolean = true;

  loadingMore: boolean = false;

  nextCycleIndex: number = 0;

  headers: DataTableHeader[] = [];

  atmSettings: ATMSettings | null = null;

  interestSettings: InterestSettings | null = null;

  showTunesRefundSheet: boolean = false;

  tunesRefundQualStart: string = '';

  tunesRefundQualEnd: string = '';


  @Inject('RewardDetailsResource')
  rewardDetailsResource!: RewardDetailsResource;

  @Inject('ATMSettingsResource')
  atmSettingsResource!: ATMSettingsResource;

  @Inject('InterestSettingsResource')
  interestSettingsResource!: InterestSettingsResource;

  static readonly TOTAL_STANDARD_HEADERS = 4;

  mounted() {
    this.loadDetails();
  }

  @Watch('account')
  async loadDetails() {
    this.loading = true;
    this.rewardItems = [];
    this.headers = [];
    this.interestSettings = null;

    if (this.fiId && this.account && this.cycles.length > 0) {
      this.atmSettings = await this.atmSettingsResource.getATMSettings(
        this.fiId,
      );

      this.interestSettings = await this.interestSettingsResource.getInterestSettings(
        this.fiId,
      );

      this.nextCycleIndex = this.cycles.length - 1;
      const currentDetails: RewardDetailTableEntry | null = await this.loadDetailsForCycle(
        this.cycles[this.nextCycleIndex--],
      );
      if (currentDetails) {
        this.rewardItems.push(currentDetails);
      }

      if (this.nextCycleIndex > -1) {
        const previousDetails = await this.loadDetailsForCycle(
          this.cycles[this.nextCycleIndex--],
        );
        if (previousDetails) {
          this.rewardItems.push(previousDetails);
        }
      }

      if (this.rewardItems.length > 1) {
        this.rewardItems.sort(this.compareQualificationCycles);
        this.rewardItems[0].cycleName = 'Current';
        this.rewardItems[1].cycleName = 'Previous';
      } else if (this.rewardItems.length === 1) {
        this.rewardItems[0].cycleName = 'Current';
      }
    }
    this.loading = false;
  }

  async loadMore() {
    if (
      this.nextCycleIndex > -1
      && this.rewardItems
      && this.rewardItems.length > 1
      && !this.loadingMore
    ) {
      this.loadingMore = true;
      const promises: Promise<RewardDetailTableEntry>[] = [];
      for (let i = 0; i < 10 && this.nextCycleIndex > -1; i++) {
        promises.push(
          this.loadDetailsForCycle(this.cycles[this.nextCycleIndex--]),
        );
      }
      // Make sure results are sorted by end date before adding to table
      await Promise.all(promises).then(
        (values: (RewardDetailTableEntry)[]) => {
          values.sort(this.compareQualificationCycles);
          const parent: RewardDetailTable = this;
          values.forEach((element) => {
            if (element) {
              parent.rewardItems.push(element);
            }
          });
        },
      );
      this.loadingMore = false;
    }
  }

  // The order of this sort is reversed so the result is in descending order
  compareQualificationCycles = (reward1: RewardDetailTableEntry | null, reward2: RewardDetailTableEntry | null): number => {
    if (reward1 == null || reward2 == null) {
      return 0;
    } if (reward1.sortDate > reward2.sortDate) {
      return -1;
    } if (reward1.sortDate < reward2.sortDate) {
      return 1;
    }
    return 0;
  }

  async loadDetailsForCycle(
    cycle: Cycle,
  ): Promise<RewardDetailTableEntry> {
    try {
      let detail: RewardDetails | null = await this.rewardDetailsResource.getRewardDetails(
        this.fiId,
        cycle.id,
        this.account.externalProductMappingId,
        this.account.accountKasasaKey,
      );

      // If we didn't get a detail back for this cycle, create an empty one
      if (!detail) {
        detail = {
          account: this.account.accountKasasaKey,
          primaryAccountHolderFullName: '',
          qualLevel: null,
          netServiceFee: null,
          atmRefunds: null,
          atmTerminalFees: null,
          atmForeignFees: null,
          cashBack: null,
          interestToDate: null,
          interestAdjustment: null,
          qualInt: null,
          nonQualInt: null,
          diffInt: null,
          tunesRefunds: null,
          eatsRewards: null,
          streamingRewards: null,
        };
      }

      const entry: RewardDetailTableEntry = RewardDetailTable.buildTableEntry(
        detail,
        cycle,
      );

      // If we only have the standard headers then we haven't encountered
      // a populated reward detail cycle yet, so try to build them
      // from this one.
      if (this.headers.length <= RewardDetailTable.TOTAL_STANDARD_HEADERS) {
        this.buildHeaders(detail);
      }

      return entry;
    } catch (error) {
      this.showErrorMessage(error);
      throw error;
    }
  }

  static buildTableEntry(
    detail: RewardDetails,
    cycle: Cycle,
  ): RewardDetailTableEntry {
    return {
      cycleId: cycle.id,
      cycleName: '',
      statementEndDate: new Date(
        cycle.statementEndDate,
      ).toLocaleDateString('en-US', { timeZone: 'UTC' }),
      sortDate: new Date(cycle.qualificationEndDate).getTime(),
      rewardDetails: detail,
      qualStartDate: new Date(cycle.qualificationStartDate)
        .toISOString()
        .substr(0, 10),
      qualEndDate: new Date(cycle.qualificationEndDate)
        .toISOString()
        .substr(0, 10),
    };
  }

  buildHeaders(details: RewardDetails) {
    this.headers = [];
    this.headers.push({ text: 'Rewards Cycle', value: 'cycleName' });
    this.headers.push({ text: 'Statement End Date', value: 'sortDate' });
    this.headers.push({ text: 'Qualified', value: 'qualified' });
    this.headers.push({
      text: 'Reward Level',
      value: 'rewardDetails.qualLevel',
    });

    if (
      this.atmSettings
      && this.atmSettings.atmRefundAggregatorSetting === 'SEPARATE'
    ) {
      if (details.atmTerminalFees !== null) {
        this.headers.push({
          text: 'ATM Terminal Fees',
          value: 'rewardDetails.atmTerminalFees',
        });
      }

      if (details.atmForeignFees !== null) {
        this.headers.push({
          text: 'ATM Foreign Fees',
          value: 'rewardDetails.atmForeignFees',
        });
      }
    } else if (details.atmRefunds !== null) {
      this.headers.push({
        text: 'ATM Fee Refunds',
        value: 'rewardDetails.atmRefunds',
      });
    }

    if (details.tunesRefunds !== null) {
      this.headers.push({
        text: 'Tunes Refunds',
        value: 'rewardDetails.tunesRefunds',
      });
    }

    if (details.cashBack !== null) {
      this.headers.push({
        text: 'Cash Back Paid',
        value: 'rewardDetails.cashBack',
      });
    }

    if (details.eatsRewards !== null) {
      this.headers.push({
        text: 'Eats Rewards Paid',
        value: 'rewardDetails.eatsRewards',
      });
    }

    if (details.streamingRewards !== null) {
      this.headers.push({
        text: 'Streaming Rewards Paid',
        value: 'rewardDetails.streamingRewards',
      });
    }

    if (details.netServiceFee !== null) {
      this.headers.push({
        text: 'Net Service Fee',
        value: 'rewardDetails.netServiceFee',
      });
    }

    if (details.interestAdjustment !== null || details.qualInt !== null) {
      if (this.interestSettings?.coreInterestRateCalculation === 'QUAL_RATE') {
        this.headers.push({
          text: 'Interest Adjustment',
          value: 'rewardDetails.interestAdjustment',
        });
      }

      this.headers.push({
        text: 'Kasasa Interest Calculation',
        value: 'rewardDetails.qualInt',
      });
    }
  }

  getTableValue = (item: RewardDetailTableEntry, header: any): String => {
    let value: String = '';

    if (header.value) {
      if (header.value === 'cycleName') {
        value = item.cycleName;
      } else if (header.value === 'sortDate') {
        value = item.statementEndDate;
      } else if (header.value === 'qualified') {
        value = item.rewardDetails.qualLevel && item.rewardDetails.qualLevel > 0
          ? 'Yes'
          : 'No';
      } else if (header.value === 'rewardDetails.qualLevel') {
        value = item.rewardDetails.qualLevel
          ? item.rewardDetails.qualLevel.toString()
          : '0';
      } else if (header.value === 'rewardDetails.atmRefunds') {
        value = RewardDetailTable.formatDollarAmount(
          item.rewardDetails.atmRefunds,
        );
      } else if (header.value === 'rewardDetails.atmTerminalFees') {
        value = RewardDetailTable.formatDollarAmount(
          item.rewardDetails.atmTerminalFees,
        );
      } else if (header.value === 'rewardDetails.atmForeignFees') {
        value = RewardDetailTable.formatDollarAmount(
          item.rewardDetails.atmForeignFees,
        );
      } else if (header.value === 'rewardDetails.tunesRefunds') {
        value = RewardDetailTable.formatDollarAmount(
          item.rewardDetails.tunesRefunds,
        );
      } else if (header.value === 'rewardDetails.cashBack') {
        value = RewardDetailTable.formatDollarAmount(
          item.rewardDetails.cashBack,
        );
      } else if (header.value === 'rewardDetails.eatsRewards') {
        value = RewardDetailTable.formatDollarAmount(
          item.rewardDetails.eatsRewards,
        );
      } else if (header.value === 'rewardDetails.streamingRewards') {
        value = RewardDetailTable.formatDollarAmount(
          item.rewardDetails.streamingRewards,
        );
      } else if (header.value === 'rewardDetails.netServiceFee') {
        value = RewardDetailTable.formatDollarAmount(
          item.rewardDetails.netServiceFee,
        );
      } else if (header.value === 'rewardDetails.interestAdjustment') {
        if (item.rewardDetails.qualLevel && item.rewardDetails.qualLevel > 0) {
          value = '-';
        } else {
          value = RewardDetailTable.formatDollarAmount(
            item.rewardDetails.interestAdjustment,
          );
        }
      } else if (header.value === 'rewardDetails.qualInt') {
        // Actually show nonqualint if not qualified
        if (item.rewardDetails.qualLevel && item.rewardDetails.qualLevel > 0) {
          value = RewardDetailTable.formatDollarAmount(
            item.rewardDetails.qualInt,
          );
        } else {
          value = RewardDetailTable.formatDollarAmount(item.rewardDetails.nonQualInt);
        }
      }
    }
    return value;
  };

  static formatDollarAmount(value: any) {
    return `$${value ? Number(value).toFixed(2) : '0.00'}`;
  }

  showTunesRefunds(item: RewardDetailTableEntry) {
    this.tunesRefundQualStart = item.qualStartDate;
    this.tunesRefundQualEnd = item.qualEndDate;
    this.showTunesRefundSheet = true;
  }

  showErrorMessage(error: string) {
    const al = new Alert(error, NoticeClass.ERROR);
    al.setTimeout(5000);
    this.$store.dispatch('notices/add', al);
  }
}
